/**
 * useSearch — debounced global-search hook for the SearchBar (Story V10-K).
 *
 * live mode → GET /search via fetchSearch (SearchResponseDto).
 * mock mode → mockSearch(q) resolved synchronously (UI renders standalone).
 *
 * The query is debounced here so keystrokes don't each fire a request. The hook is
 * disabled below a minimum length so an empty/1-char box makes no call.
 *
 * [Source: apps/web/src/features/customers/hooks/useKundeRows.ts] (live/mock query hook)
 * [Source: apps/web/src/lib/data-mode.ts] (VITE_DATA_MODE)
 * [Source: docs/stories/v10-k-global-search.md AC5]
 */

import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { qk } from '../../../lib/query-keys';
import { isLiveData } from '../../../lib/data-mode';
import { fetchSearch, mockSearch } from '../api/search-api';
import type { SearchResponseDto } from '@brokly/shared';

/** Minimum query length before a search fires (avoids noise on 1 char). */
export const SEARCH_MIN_QUERY_LENGTH = 2;

/** Debounce window (ms) between the last keystroke and the query. */
export const SEARCH_DEBOUNCE_MS = 250;

/**
 * Debounce a changing value by `delay` ms (UI-state only — coding-standards §3).
 */
function useDebounced<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

/**
 * Run the debounced hybrid search for the current raw input.
 *
 * @param rawQuery the live input value (undebounced)
 */
export function useSearch(rawQuery: string) {
  const debounced = useDebounced(rawQuery.trim(), SEARCH_DEBOUNCE_MS);
  const enabled = debounced.length >= SEARCH_MIN_QUERY_LENGTH;
  const live = isLiveData();

  return useQuery<SearchResponseDto>({
    queryKey: qk.search(debounced),
    queryFn: () => (live ? fetchSearch(debounced) : Promise.resolve(mockSearch(debounced))),
    enabled,
    // keep the previous results visible while the next query resolves (no flicker)
    placeholderData: (prev) => prev,
    staleTime: 30_000,
  });
}
